// @ts-nocheck
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faXmark, 
    faVideo, 
    faLink,
    faList,
    faUpload
} from '@fortawesome/free-solid-svg-icons';

interface AddVideoModalProps {
    onClose: () => void;
    onAddVideo: (url: string) => Promise<void>;
    onSyncChannel: (channelUrl: string) => void;
}

const AddVideoModal: React.FC<AddVideoModalProps> = ({ onClose, onAddVideo, onSyncChannel }) => {
    const [activeTab, setActiveTab] = useState<'video' | 'channel'>('video');
    const [videoUrl, setVideoUrl] = useState('');
    const [channelUrl, setChannelUrl] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateYouTubeUrl = (url: string): boolean => {
        const patterns = [
            /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/)|youtu\.be\/)[\w-]+/,
            /^(https?:\/\/)?(www\.)?youtube\.com\/(channel|c|@)\/[\w-]+/
        ];
        return patterns.some(pattern => pattern.test(url));
    };

    const handleSubmit = async () => {
        setError('');

        if (activeTab === 'video') {
            if (!videoUrl.trim()) {
                setError('Please enter a YouTube video URL');
                return;
            }
            if (!validateYouTubeUrl(videoUrl)) {
                setError('Please enter a valid YouTube video URL');
                return;
            }
            
            setIsSubmitting(true);
            try {
                await onAddVideo(videoUrl);
                // Modal will be closed by the parent component on success
            } catch (error) {
                setError('Failed to add video. Please try again.');
            } finally {
                setIsSubmitting(false);
            }
        } else {
            if (!channelUrl.trim()) {
                setError('Please enter a YouTube channel URL');
                return;
            }
            if (!channelUrl.includes('youtube.com/')) {
                setError('Please enter a valid YouTube channel URL');
                return;
            }
            onSyncChannel(channelUrl);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <h2 className="text-xl font-semibold text-gray-800">
                        Add YouTube Content
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <FontAwesomeIcon icon={faXmark} className="text-xl" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b">
                    <button
                        onClick={() => setActiveTab('video')}
                        className={`flex-1 px-4 py-3 text-sm font-medium ${
                            activeTab === 'video'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <FontAwesomeIcon icon={faVideo} className="mr-2" />
                        Single Video
                    </button>
                    <button
                        onClick={() => setActiveTab('channel')}
                        className={`flex-1 px-4 py-3 text-sm font-medium ${
                            activeTab === 'channel'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <FontAwesomeIcon icon={faList} className="mr-2" />
                        Channel Sync
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {activeTab === 'video' ? (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                YouTube Video URL
                            </label>
                            <div className="relative">
                                <FontAwesomeIcon 
                                    icon={faLink} 
                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                />
                                <input
                                    type="url"
                                    value={videoUrl}
                                    onChange={(e) => setVideoUrl(e.target.value)}
                                    placeholder="https://youtube.com/watch?v=..."
                                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <p className="text-sm text-gray-600 mt-2">
                                Video will be added for review before processing
                            </p>
                        </div>
                    ) : (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                YouTube Channel URL
                            </label>
                            <div className="relative">
                                <FontAwesomeIcon 
                                    icon={faLink} 
                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                />
                                <input
                                    type="url"
                                    value={channelUrl}
                                    onChange={(e) => setChannelUrl(e.target.value)}
                                    placeholder="https://youtube.com/@channelname"
                                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <p className="text-sm text-gray-600 mt-2">
                                This will sync all videos from the channel for review
                            </p>
                        </div>
                    )}

                    {error && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                            {error}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
                    <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Processing...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faUpload} />
                                {activeTab === 'video' ? 'Add Video' : 'Sync Channel'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddVideoModal;